import moment from 'moment';
import { emptyToNull, parseApiDate, toApiDate } from '../leaseGeneralInvoiceReceipt/formUtils';
import { fmt3, num, round3 } from '../leaseGeneralInvoiceReceipt/allocationHelpers';
import { COST_CENTRE_LEVELS, EWA_TYPE, NARRATION_MAX } from './saleVoucherConstants';

export { fmt3, num, round3 };

const NO_TAX = '0';
let rowKey = 0;

/** A blank grid row. Cost centres and the tax code default from the header. */
export const emptyLine = (defaults = {}) => ({
    key: `row-${++rowKey}`,
    acCode: '', acName: '', enteredAmount: '', taxCode: defaults.taxCode || NO_TAX, percentage: 0, narration: '',
    cc1: defaults.cc1 || '', cc2: defaults.cc2 || '', cc3: defaults.cc3 || '', cc4: defaults.cc4 || '',
    credit: 0, vat: 0, st: 0, total: 0, converted: 0,
});

export const initialForm = () => ({
    postingDate: new Date(),
    voucherType: '',
    partyType: 'All',
    useCoa: false,
    customer: null,
    refNo: '', invoiceNo: '', invoiceDate: null, dueDate: null, orderNo: '', orderDate: null,
    currency: '',
    conversionRate: 1,
    amount: '',
    narration: '',
    taxCode: NO_TAX,
    taxInclusive: false,
    cc1: '', cc2: '', cc3: '', cc4: '',
    copyCC: false,
    batchNo: '',
    lines: [emptyLine()],
});

/** A row with no account and a zero amount is dropped on save. */
export const isBlankLine = (line) => !`${line.acCode || ''}`.trim() && num(line.enteredAmount) === 0;

/**
 * Preview of the figures the server derives (rounded to 3 decimals at every step).
 * vatRate and salesTaxRate are percents (5 means 5%). A tax that is not effective yet at the posting date is 0%.
 */
export const computeLineTax = ({ enteredAmount, taxInclusive, vatRate = 0, salesTaxRate = 0, taxFromDate = null, postingDate = null }) => {
    const effective = !(taxFromDate && postingDate && moment(taxFromDate).isAfter(moment(postingDate), 'day'));
    const vat = effective ? num(vatRate) / 100 : 0;
    const st = effective ? num(salesTaxRate) / 100 : 0;
    const entered = round3(enteredAmount);

    if (taxInclusive) {
        const vatAmount = round3(round3(entered / (1 + vat)) * vat);
        const stAmount = round3(round3(entered / (1 + st)) * st);
        const credit = round3(entered - (vatAmount + stAmount));
        return { credit, vat: vatAmount, st: stAmount, total: round3(credit + vatAmount + stAmount) };
    }
    const credit = entered;
    const vatAmount = round3(credit * vat);
    const stAmount = round3(credit * st);
    return { credit, vat: vatAmount, st: stAmount, total: round3(credit + vatAmount + stAmount) };
};

const findTax = (taxCodes, code) => (taxCodes || []).find((t) => `${t.taxCode}` === `${code}`);

/** Recomputes VAT / ST / Total / Converted on every line. Never touches what the user typed. */
export const recomputeGrid = (form, taxCodes) => ({
    ...form,
    lines: (form.lines || []).map((line) => {
        const tax = line.taxCode === NO_TAX ? null : findTax(taxCodes, line.taxCode);
        const figures = computeLineTax({
            enteredAmount: line.enteredAmount,
            taxInclusive: form.taxInclusive,
            vatRate: tax?.rate || 0,
            salesTaxRate: tax?.salesTaxRate || 0,
            taxFromDate: tax?.fromDate ? parseApiDate(tax.fromDate) : null,
            postingDate: form.postingDate,
        });
        return {
            ...line, ...figures,
            converted: round3(num(form.conversionRate) * figures.total),
            vatAcCode: tax?.vatAcCode, stAcCode: tax?.stAcCode,
        };
    }),
});

/** Footer figures: lines total, count of real lines, and the difference against the header Amount. */
export const gridTotals = (form) => {
    const real = (form.lines || []).filter((l) => !isBlankLine(l));
    const linesTotal = round3(real.reduce((sum, l) => sum + num(l.total), 0));
    return { linesTotal, count: real.length, difference: round3(num(form.amount) - linesTotal) };
};

/** Copies the header cost centres onto every line. */
export const applyCopyCostCentres = (form) => ({
    ...form,
    lines: (form.lines || []).map((l) => ({ ...l, cc1: form.cc1, cc2: form.cc2, cc3: form.cc3, cc4: form.cc4 })),
});

/** Changing level n clears levels n+1..4. values uses cc1..cc4 keys. */
export const clearChildCostCentres = (values, changedLevel) => {
    const next = { ...values };
    COST_CENTRE_LEVELS.filter((n) => n > changedLevel).forEach((n) => { next[`cc${n}`] = ''; });
    return next;
};

/** Ordered list of messages (4.1 header, 4.2 first failing line rule, 4.3 save). [] when the form is valid. */
export const validateVoucher = (form, mode, settings, amountReceived = 0) => {
    const messages = [];
    const required = (n) => settings?.[`isCC${n}Required`] === true;

    // 4.1 header
    if (!form.postingDate) messages.push('Enter Voucher Date.');
    if (!form.voucherType) messages.push('Select Type.');
    if (!form.customer?.value) messages.push('Select Debit Account.');
    if (!(num(form.amount) > 0)) messages.push('Debit amount should not be zero.');
    if (!(num(form.conversionRate) > 0)) messages.push('Conversion rate must be greater than zero.');
    if (`${form.narration || ''}`.length > NARRATION_MAX) messages.push(`Narration cannot exceed ${NARRATION_MAX} characters.`);
    if (form.voucherType === EWA_TYPE && mode === 'create' && !`${form.batchNo || ''}`.trim()) messages.push('Select EWA Reading');
    COST_CENTRE_LEVELS.forEach((n) => {
        if (required(n) && !`${form[`cc${n}`] || ''}`.trim()) messages.push(`Cost Center ${n} Required.`);
    });

    // 4.2 lines, stopping at the first failure
    const real = (form.lines || []).filter((l) => !isBlankLine(l));
    const hasAccount = (l) => !!`${l.acCode || ''}`.trim();
    let lineError = null;
    if (real.length === 0) lineError = 'Incomplete Data Entry.';
    else if (real.some((l) => num(l.enteredAmount) !== 0 && !hasAccount(l))) lineError = 'Some Invalid Account Code/Name Found.';
    else if (real.some((l) => hasAccount(l) && num(l.enteredAmount) === 0)) lineError = 'Credit Amount should not be zero.';
    else if (real.some((l) => num(l.enteredAmount) < 0)) lineError = 'Credit Amount cannot be negative.';
    else if (!form.copyCC) {
        const missing = COST_CENTRE_LEVELS.find((n) => required(n) && real.some((l) => !`${l[`cc${n}`] || ''}`.trim()));
        if (missing) lineError = `Cost Center ${missing} Required.`;
    }
    if (lineError) messages.push(lineError);

    // 4.3 save
    if (!lineError && round3(gridTotals(form).linesTotal) !== round3(form.amount)) messages.push('Debit/Credit amount does not match.');
    if (mode === 'edit' && num(form.amount) < num(amountReceived)) {
        messages.push(`Amount cannot be less than the amount already received (${fmt3(amountReceived)}).`);
    }
    return messages;
};

// Cost centres 3 and 4 take free text: send it trimmed and otherwise unchanged (the server matches on the string).
const cc = (value) => `${value ?? ''}`.trim();

/** Body for create / update. Sends enteredAmount only: vat, st, total and credit are derived by the server. */
export const toSavePayload =(form, mode, financialYear) => {
    const copy = form.copyCC;
    const body = {
        postingDate: toApiDate(form.postingDate),
        voucherType: form.voucherType,
        customerNo: form.customer?.value,
        refNo: emptyToNull(form.refNo),
        invoiceNo: emptyToNull(form.invoiceNo),
        invoiceDate: toApiDate(form.invoiceDate),
        dueDate: toApiDate(form.dueDate),
        orderNo: emptyToNull(form.orderNo),
        orderDate: toApiDate(form.orderDate),
        currency: form.currency,
        conversionRate: num(form.conversionRate),
        amount: round3(form.amount),
        narration: form.narration || '',
        taxCode: form.taxCode || NO_TAX,
        taxInclusive: !!form.taxInclusive,
        costCenter1: cc(form.cc1), costCenter2: cc(form.cc2), costCenter3: cc(form.cc3), costCenter4: cc(form.cc4),
        copyCostCentres: !!copy,
        batchNo: form.voucherType === EWA_TYPE ? emptyToNull(form.batchNo) : null,
        lines: (form.lines || []).filter((l) => !isBlankLine(l)).map((l) => ({
            acCode: `${l.acCode}`.trim(),
            enteredAmount: round3(l.enteredAmount),
            taxCode: l.taxCode || NO_TAX,
            percentage: num(l.percentage),
            narration: l.narration || '',
            costCenter1: cc(copy ? form.cc1 : l.cc1), costCenter2: cc(copy ? form.cc2 : l.cc2),
            costCenter3: cc(copy ? form.cc3 : l.cc3), costCenter4: cc(copy ? form.cc4 : l.cc4),
        })),
    };
    if (mode === 'edit') body.financialYear = financialYear;
    return body;
};

/** Loaded voucher to form state. The grid Amount cell gets enteredAmount back. */
export const mapDetailToForm = (detail) => {
    const h = detail?.header || {};
    const lines = (detail?.lines || []).map((l) => ({
        ...emptyLine(),
        acCode: l.acCode || '', acName: l.acName || '', enteredAmount: l.enteredAmount ?? '', taxCode: l.taxCode || NO_TAX,
        percentage: l.percentage || 0, narration: l.narration || '',
        cc1: l.costCenter1 || '', cc2: l.costCenter2 || '', cc3: l.costCenter3 || '', cc4: l.costCenter4 || '',
        credit: l.credit || 0, vat: l.vat || 0, st: l.st || 0, total: l.total || 0, converted: l.convertedAmount || 0,
    }));
    return {
        ...initialForm(),
        postingDate: parseApiDate(h.postingDate) || new Date(),
        voucherType: h.voucherType || '',
        customer: h.customerNo ? { value: h.customerNo, label: `${h.customerNo} — ${h.customerName || ''}` } : null,
        refNo: h.refNo || '', invoiceNo: h.invoiceNo || '', invoiceDate: parseApiDate(h.invoiceDate),
        dueDate: parseApiDate(h.dueDate), orderNo: h.orderNo || '', orderDate: parseApiDate(h.orderDate),
        currency: h.currency || '', conversionRate: h.conversionRate || 1,
        amount: h.amount ?? '', narration: h.narration || '',
        taxCode: h.taxCode || NO_TAX, taxInclusive: !!h.taxInclusive,
        cc1: h.costCenter1 || '', cc2: h.costCenter2 || '', cc3: h.costCenter3 || '', cc4: h.costCenter4 || '',
        batchNo: h.batchNo || '',
        lines: lines.length ? lines : [emptyLine()],
    };
};
